import React, { useState, useEffect } from 'react';
import { FileText, Search, Eye, X } from 'lucide-react';
import * as utils from '../lib/utils.js';
import { cn } from '../lib/utils';

const RecentDocuments = () => {


  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Simulate loading time
    const fetchDocuments = async () => {
      try {
        // Replace with actual API call
        const query = `?createdOn=${new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US')}`; // Gets date 7 days ago in MM-DD-YYYY format
        const res = await utils.getDocuments(query);
        console.log('Documents fetched:', res.data.documents);
        
        // Sort documents by date (newest first)
        const sortedDocuments = [...res.data.documents].sort((a, b) => {
          const dateA = a.fields?.Modified ? new Date(a.fields.Modified) : new Date(0);
          const dateB = b.fields?.Modified ? new Date(b.fields.Modified) : new Date(0);
          return dateB - dateA; // Descending order (newest first)
        });
        
        setDocuments(sortedDocuments);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching documents:', err);
        setError('Failed to load documents');
        setLoading(false);
      }
    }
    fetchDocuments();

  }, []);

  useEffect(() => {
    // Filter documents based on search query
    if (!searchQuery.trim()) {
      setFilteredDocuments(documents);
      return;
    }

    const lowerCaseQuery = searchQuery.toLowerCase();
    const filtered = documents.filter(doc => 
      doc.fields?.FileLeafRef?.toLowerCase().includes(lowerCaseQuery) ||
      doc.fields?.DocumentType?.toLowerCase().includes(lowerCaseQuery) ||
      doc.fields?.ShipmentNumber?.toLowerCase().includes(lowerCaseQuery)
    );
    
    // Sort filtered documents by date (newest first)
    const sortedFilteredDocuments = [...filtered].sort((a, b) => {
      const dateA = a.fields?.Modified ? new Date(a.fields.Modified) : new Date(0);
      const dateB = b.fields?.Modified ? new Date(b.fields.Modified) : new Date(0);
      return dateB - dateA; // Descending order (newest first)
    });
    
    setFilteredDocuments(sortedFilteredDocuments);
  }, [searchQuery, documents]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4 p-6">
        <div className="h-10 bg-gray-200 rounded w-full"></div>
        <div className="h-10 bg-gray-200 rounded w-full"></div>
        <div className="h-10 bg-gray-200 rounded w-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex items-center">
          <div className="flex-shrink-0 text-red-500 mr-2">
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-bold text-gray-800 mb-6">Recent Documents (last 7 days)</h2>
      
      {/* Search bar */}
      <div className="relative mb-6">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search size={18} className="text-gray-400" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Search documents..."
          className="pl-10 pr-10 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
        />
        {searchQuery && (
          <button
            onClick={clearSearch}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <X size={18} className="text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>

      {/* Documents table */}
      <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Document</th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Type</th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Shipment</th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Date</th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Size</th>
              <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                <span className="sr-only">View</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {filteredDocuments.length > 0 ? (
              filteredDocuments.map((doc) => (
                <tr key={doc.id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <span className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-accent-50 text-accent-700">
                          {doc.fields?.DocumentType && utils.getDocumentTypeIcon(doc.fields.DocumentType)}
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="font-medium text-gray-900">{doc.fields?.FileLeafRef}</div>
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {doc.fields?.DocumentType || 'Unknown'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {doc.fields?.ShipmentNumber || '-'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {doc.fields?.Modified ? new Date(doc.fields.Modified).toLocaleDateString() : '-'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {doc.fields?.FileSizeDisplay ? utils.formatFileSize(doc.fields.FileSizeDisplay) : '-'}
                  </td>
                  <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                    {doc.webUrl && (
                      <button
                        onClick={() => window.open(doc.webUrl, '_blank')}
                        className="text-accent-600 hover:text-accent-900 flex items-center justify-center"
                        title="View document"
                      >
                        <Eye size={18} />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="px-6 py-10 text-center">
                  <FileText size={36} className="mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-500">
                    {searchQuery ? 'No documents matching your search' : 'No documents available'}
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RecentDocuments;
